import {
  MessageAttributeValue,
  SQSClient,
  SendMessageCommand,
} from "@aws-sdk/client-sqs"
import { eq, and, isNotNull } from "drizzle-orm"
import { supportingDocument } from "@tc/drizzle-v2"
import { DbTransaction } from "@tc/middy-decorators"
import { searchBuilderLog } from "../utils/logger"
import {
  formatDocumentType,
  resolveForeignPremisesId,
} from "../utils/supporting-documents-helper"

const SUPPORTING_DOCUMENTS_COLLECTION = "supporting-documents"

export type SupportingDocumentSearchDocument = {
  id: string
  status: string | null
  document_type: string | null
  document_number: string | null
  document_sequence: number | null
  document_reference_number: string | null
  primary_operator: string | null
  intended_operators: string[] | null
  origin_country: string | null
  product_description: string | null
  identification_marks: string[] | null
  foreign_manufacture_premises: string | null
  foreign_premises_id: string | null
  file_id: string | null
  nz_manufacture_premises_id: string | null
  nz_manufacture_premises_name: string | null
  production_dates: object[] | null
  supporting_document_file_id: string | null
  created_at: string | null
  updated_at: string | null
  update_ts: number | null
  accessible_by: string[] | null
  is_linked: boolean
}

const client = new SQSClient({})

export const saveSupportingDocumentToSearch = async (
  tx: DbTransaction,
  supportingDocumentId: string,
  collectionName?: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const targetCollection = collectionName ?? SUPPORTING_DOCUMENTS_COLLECTION
  // Only fetch AP documents (those with document_number)
  const document = await tx
    .select({
      id: supportingDocument.id,
      status: supportingDocument.status,
      documentType: supportingDocument.documentType,
      documentNumber: supportingDocument.documentNumber,
      documentSequence: supportingDocument.documentSequence,
      documentReferenceNumber: supportingDocument.documentReferenceNumber,
      primaryOperator: supportingDocument.primaryOperator,
      primaryOperatorBlob: supportingDocument.primaryOperatorBlob,
      intendedOperators: supportingDocument.intendedOperators,
      originCountry: supportingDocument.originCountry,
      productDescription: supportingDocument.productDescription,
      identificationMarks: supportingDocument.identificationMarks,
      foreignManufacturePremisesIds:
        supportingDocument.foreignManufacturePremisesIds,
      foreignManufacturePremisesNames:
        supportingDocument.foreignManufacturePremisesNames,
      nzManufacturePremiseId: supportingDocument.nzManufacturePremiseId,
      nzManufacturePremisesName: supportingDocument.nzManufacturePremisesName,
      productionDates: supportingDocument.productionDates,
      fileId: supportingDocument.fileId,
      createdAt: supportingDocument.createdAt,
      updatedAt: supportingDocument.updatedAt,
      is_linked: supportingDocument.isLinked,
    })
    .from(supportingDocument)
    .where(
      and(
        eq(supportingDocument.id, supportingDocumentId),
        isNotNull(supportingDocument.documentNumber),
      ),
    )

  if (document.length === 0) {
    return
  }

  const rawDoc = document[0]

  // For search: use name (allows searching by operator name)
  // For accessible_by filter: use id (matches FGP registration IDs)
  let primaryOperatorName: string | null = null
  let primaryOperatorId: string | null = null
  if (rawDoc.primaryOperatorBlob) {
    const blob = rawDoc.primaryOperatorBlob as { id?: string; name?: string }
    primaryOperatorName = blob.name ?? blob.id ?? null
    primaryOperatorId = blob.id ?? blob.name ?? null
  }

  const intendedOperatorNames: string[] = []
  const intendedOperatorIds: string[] = []
  if (rawDoc.intendedOperators && Array.isArray(rawDoc.intendedOperators)) {
    for (const op of rawDoc.intendedOperators) {
      const operator = op as { id?: string; name?: string }
      if (operator.name ?? operator.id) {
        intendedOperatorNames.push((operator.name ?? operator.id) as string)
      }
      if (operator.id ?? operator.name) {
        intendedOperatorIds.push((operator.id ?? operator.name) as string)
      }
    }
  }

  const originCountryName: string | null = rawDoc.originCountry
    ? ((rawDoc.originCountry as { name?: string })?.name ?? null)
    : null

  // accessible_by uses IDs for permission filtering
  const accessibleBy: string[] = []
  if (primaryOperatorId) {
    accessibleBy.push(primaryOperatorId)
  }
  accessibleBy.push(...intendedOperatorIds)

  const identificationMarks =
    rawDoc.identificationMarks && Array.isArray(rawDoc.identificationMarks)
      ? rawDoc.identificationMarks.filter((mark) => typeof mark === "string")
      : null

  const doc: SupportingDocumentSearchDocument = {
    id: rawDoc.id,
    status: rawDoc.status ?? null,
    document_type: formatDocumentType(rawDoc.documentType ?? null),
    document_number: rawDoc.documentNumber ?? null,
    document_sequence: rawDoc.documentSequence ?? null,
    document_reference_number: rawDoc.documentReferenceNumber ?? null,
    primary_operator: primaryOperatorName ?? null,
    intended_operators:
      intendedOperatorNames.length > 0 ? intendedOperatorNames : null,
    origin_country: originCountryName ?? null,
    product_description: rawDoc.productDescription ?? null,
    identification_marks: identificationMarks ?? null,
    foreign_manufacture_premises:
      rawDoc.foreignManufacturePremisesNames ?? null,
    foreign_premises_id: resolveForeignPremisesId(
      rawDoc.foreignManufacturePremisesIds,
    ),
    file_id: rawDoc.fileId ?? null,
    nz_manufacture_premises_id: rawDoc.nzManufacturePremiseId ?? null,
    nz_manufacture_premises_name: rawDoc.nzManufacturePremisesName ?? null,
    production_dates: (rawDoc.productionDates as object[] | null) ?? null,
    supporting_document_file_id: rawDoc.fileId ?? null,
    created_at: rawDoc.createdAt?.toString().replace(/\s*\(.*\)$/, "") ?? null,
    updated_at: rawDoc.updatedAt?.toString().replace(/\s*\(.*\)$/, "") ?? null,
    update_ts: rawDoc.updatedAt
      ? new Date(rawDoc.updatedAt.toString()).getTime()
      : null,
    accessible_by: accessibleBy.length > 0 ? accessibleBy : null,
    is_linked: rawDoc.is_linked,
  }

  if (options?.returnDoc) return doc as never

  searchBuilderLog.debug({
    message:
      "Sending message with Supporting Document Details to Search service via SQS",
    supportingDocumentId: supportingDocumentId,
    collection: targetCollection,
    document: doc,
  })

  const attributes: Record<string, MessageAttributeValue> = {
    collection: {
      StringValue: targetCollection,
      DataType: "String",
    },
  }

  const command = new SendMessageCommand({
    QueueUrl: process.env["QUEUE_URL"],
    MessageBody: JSON.stringify(doc),
    MessageAttributes: attributes,
  })

  try {
    await client.send(command)
  } catch (error) {
    console.error("Error sending supporting document to search:", error)
    throw error
  }

  searchBuilderLog.info({
    message:
      "Successfully sent message with Supporting Document Details to Search service via SQS",
    supportingDocumentId: supportingDocumentId,
    collection: targetCollection,
  })
}
